import React, { useEffect, useState, useRef } from 'react';
import { MarketLink as Link } from './MarketLink';
import {
    X,
    Home,
    List,
    ShoppingBag,
    Heart,
    Info,
    Settings,
    Headphones,
    Call,
    LogOut,
    ChevronRight,
    Store,
    Globe,
    FileText,
    ShieldCheck,
    Truck,
    HelpCircle,
    Radio,
    Video,
    Crown,
    Users,
    BookOpen,
    Sparkles,
    Flame
} from 'lucide-react';
import { getTranslation as t, isRTL } from '../utils/translations';
import { useAuth } from '../context/AuthContext';
import { getCategories } from '../api/client';
import LanguageToggle from './LanguageToggle';
import RegionSelector from './RegionSelector';

const MobileSidebar = ({ isOpen, onClose, storeSettings, isDarkMode, toggleTheme, themeMode = 'auto' }) => {
    const { isAuthenticated, logout, user } = useAuth();
    const [categories, setCategories] = useState([]);
    const closeBtnRef = useRef(null);
    const touchInfo = useRef({ startX: 0, startY: 0, currentX: 0, currentY: 0 });

    const isAr = isRTL();

    const handleClose = () => {
        onClose();
        if (typeof window !== 'undefined' && window.history.state?.luxvonoModal === 'sidebar') {
            window.history.back();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('mobile-sidebar-open');
            if (typeof window !== 'undefined') {
                window.history.pushState({ luxvonoModal: 'sidebar' }, '');
            }
            requestAnimationFrame(() => {
                closeBtnRef.current?.focus();
            });
        } else {
            document.body.classList.remove('mobile-sidebar-open');
            document.getElementById('mobile-hamburger-btn')?.focus();
        }

        const handlePopState = () => {
            if (isOpen) {
                onClose();
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            document.body.classList.remove('mobile-sidebar-open');
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, onClose]);

    const onTouchStart = (e) => {
        const touch = e.targetTouches[0];
        touchInfo.current = {
            startX: touch.clientX,
            startY: touch.clientY,
            currentX: touch.clientX,
            currentY: touch.clientY
        };
    };

    const onTouchMove = (e) => {
        const touch = e.targetTouches[0];
        touchInfo.current.currentX = touch.clientX;
        touchInfo.current.currentY = touch.clientY;
    };

    const onTouchEnd = () => {
        const { startX, startY, currentX, currentY } = touchInfo.current;
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        const minSwipe = 50;

        if (Math.abs(deltaX) > minSwipe && Math.abs(deltaX) > Math.abs(deltaY)) {
            if (!isAr) {
                if (deltaX < 0) onClose();
            } else {
                if (deltaX > 0) onClose();
            }
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        getCategories()
            .then(items => {
                if (Array.isArray(items) && items.length > 0) {
                    setCategories(items);
                } else {
                    setCategories([]);
                }
            })
            .catch(() => setCategories([]));
    }, [isOpen]);

    const defaultCategories = [
        { id: 1, name: isAr ? 'ساعات فاخرة' : 'Luxury Watches', path: '/category/watches' },
        { id: 2, name: isAr ? 'مجوهرات راقية' : 'Fine Jewelry', path: '/category/jewelry' },
        { id: 3, name: isAr ? 'أزياء راقية' : 'Haute Couture', path: '/category/fashion' },
        { id: 4, name: isAr ? 'يخوت وطائرات' : 'Yachts & Jets', path: '/category/yachts' },
        { id: 5, name: isAr ? 'حقائب مصممين' : 'Designer Bags', path: '/category/bags' },
        { id: 6, name: isAr ? 'عطور فاخرة' : 'Premium Perfumes', path: '/category/perfumes' },
        { id: 7, name: isAr ? 'سيارات فاخرة' : 'Luxury Cars', path: '/category/supercars' },
        { id: 8, name: isAr ? 'إلكترونيات ذكية' : 'Smart Electronics', path: '/category/electronics' }
    ];

    const displayCategories = categories.length > 0
        ? categories.map(c => ({
            id: c.id || c.name,
            name: isAr ? (c.nameAr || c.name || '') : (c.nameEn || c.name || ''),
            path: `/category/${encodeURIComponent(c.name || '')}`
        }))
        : defaultCategories;

    const openSupportChat = () => {
        window.dispatchEvent(new CustomEvent('luxvono-chat:open', { detail: { tab: 'support' } }));
        onClose();
    };

    const userName = isAuthenticated
        ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'Luxvono Member'
        : '';
    const userInitial = isAuthenticated && userName
        ? userName.charAt(0).toUpperCase()
        : '?';

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                body.mobile-sidebar-open {
                    overflow: hidden !important;
                    touch-action: none !important;
                }

                .ms-android-backdrop {
                    position: fixed !important;
                    inset: 0 !important;
                    z-index: 99998 !important;
                    opacity: 0 !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                    background: rgba(0, 0, 0, 0.75) !important;
                    backdrop-filter: blur(10px) !important;
                    -webkit-backdrop-filter: blur(10px) !important;
                    transition: opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
                }

                .ms-android-backdrop.open {
                    opacity: 1 !important;
                    visibility: visible !important;
                    pointer-events: auto !important;
                }

                .ms-android-drawer {
                    position: fixed !important;
                    top: 0 !important;
                    bottom: 0 !important;
                    width: 310px !important;
                    max-width: 85vw !important;
                    height: 100dvh !important;
                    z-index: 99999 !important;
                    display: flex !important;
                    flex-direction: column !important;
                    background: #0F0F11 !important;
                    color: #E5E5E5 !important;
                    font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                    box-shadow: 0 0 40px rgba(0, 0, 0, 0.8) !important;
                    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.35s cubic-bezier(0.16, 1, 0.3, 1) !important;
                    overflow: hidden !important;
                    overflow-x: hidden !important;
                    touch-action: pan-y !important;
                    box-sizing: border-box !important;
                }

                @keyframes pulseLiveDot {
                    0% { transform: scale(0.85); opacity: 0.8; }
                    50% { transform: scale(1.25); opacity: 1; }
                    100% { transform: scale(0.85); opacity: 0.8; }
                }

                .ms-live-dot {
                    width: 7px !important;
                    height: 7px !important;
                    border-radius: 50% !important;
                    background: #FE2C55 !important;
                    box-shadow: 0 0 8px #FE2C55 !important;
                    animation: pulseLiveDot 1.4s infinite !important;
                    display: inline-block !important;
                }

                /* LTR layout */
                html[dir="ltr"] .ms-android-drawer,
                body[dir="ltr"] .ms-android-drawer,
                .ms-android-drawer.ltr-mode {
                    left: 0 !important;
                    right: auto !important;
                    transform: translateX(-100%) !important;
                }
                html[dir="ltr"] .ms-android-drawer.open,
                body[dir="ltr"] .ms-android-drawer.open,
                .ms-android-drawer.ltr-mode.open {
                    visibility: visible !important;
                    pointer-events: auto !important;
                    transform: translateX(0) !important;
                }

                /* RTL layout */
                html[dir="rtl"] .ms-android-drawer,
                body[dir="rtl"] .ms-android-drawer,
                .rtl-layout .ms-android-drawer,
                .ms-android-drawer.rtl-mode {
                    right: 0 !important;
                    left: auto !important;
                    transform: translateX(100%) !important;
                }
                html[dir="rtl"] .ms-android-drawer.open,
                body[dir="rtl"] .ms-android-drawer.open,
                .rtl-layout .ms-android-drawer.open,
                .ms-android-drawer.rtl-mode.open {
                    visibility: visible !important;
                    pointer-events: auto !important;
                    transform: translateX(0) !important;
                }

                .ms-android-header {
                    background: linear-gradient(180deg, #1C1C1E 0%, #0F0F11 100%) !important;
                    padding: 24px 18px 18px 18px !important;
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 16px !important;
                    border-bottom: 1px solid #1F1F22 !important;
                }

                .ms-android-brand-row {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                }

                .ms-android-brand {
                    font-size: 20px !important;
                    font-weight: 800 !important;
                    letter-spacing: 3px !important;
                    color: #FFFFFF !important;
                    margin: 0 !important;
                }

                .ms-android-close-btn {
                    width: 32px !important;
                    height: 32px !important;
                    border-radius: 50% !important;
                    background: #1F1F22 !important;
                    border: 1px solid #333333 !important;
                    color: #FFFFFF !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    cursor: pointer !important;
                    outline: none !important;
                }

                .ms-android-user-row {
                    display: flex !important;
                    align-items: center !important;
                    gap: 14px !important;
                }

                .ms-android-avatar {
                    width: 52px !important;
                    height: 52px !important;
                    border-radius: 50% !important;
                    background: #1F1F22 !important;
                    border: 1.5px solid #FFFFFF !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    color: #FFFFFF !important;
                    font-size: 20px !important;
                    font-weight: 700 !important;
                    flex-shrink: 0 !important;
                }

                .ms-android-user-meta {
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 4px !important;
                    min-width: 0 !important;
                }

                .ms-android-username {
                    font-size: 15px !important;
                    font-weight: 700 !important;
                    color: #FFFFFF !important;
                    margin: 0 !important;
                    white-space: nowrap !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                }

                .ms-android-badge {
                    display: inline-flex !important;
                    align-items: center !important;
                    gap: 4px !important;
                    padding: 2px 6px !important;
                    background: rgba(255, 255, 255, 0.1) !important;
                    border: 1px solid rgba(255, 255, 255, 0.4) !important;
                    border-radius: 3px !important;
                    font-size: 8.5px !important;
                    font-weight: 700 !important;
                    letter-spacing: 0.5px !important;
                    color: #FFFFFF !important;
                    width: fit-content !important;
                }

                .ms-android-badge-dot {
                    width: 4px !important;
                    height: 4px !important;
                    border-radius: 50% !important;
                    background: #FFFFFF !important;
                }

                .ms-android-guest-sub {
                    font-size: 11.5px !important;
                    color: #888888 !important;
                    margin: 0 !important;
                }

                .ms-android-login-btn {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: 100% !important;
                    height: 38px !important;
                    background: #000000 !important;
                    border: 1px solid #333333 !important;
                    border-radius: 4px !important;
                    color: #FFFFFF !important;
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    text-decoration: none !important;
                    cursor: pointer !important;
                }

                /* Alibaba Quick Actions Bar */
                .ms-android-quick-bar {
                    display: grid !important;
                    grid-template-columns: repeat(4, 1fr) !important;
                    gap: 6px !important;
                    background: #131316 !important;
                    padding: 12px 10px !important;
                    border-bottom: 1px solid #1F1F22 !important;
                }

                .ms-android-quick-card {
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 4px !important;
                    padding: 8px 4px !important;
                    background: rgba(255, 255, 255, 0.03) !important;
                    border: 1px solid rgba(255, 255, 255, 0.06) !important;
                    border-radius: 8px !important;
                    color: #E5E5E5 !important;
                    text-decoration: none !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                }

                .ms-android-quick-card:active {
                    background: rgba(254, 44, 85, 0.1) !important;
                    border-color: #FE2C55 !important;
                }

                .ms-android-quick-icon {
                    color: #E5E5E5 !important;
                }

                .ms-android-quick-label {
                    font-size: 10.5px !important;
                    font-weight: 600 !important;
                    color: #CCCCCC !important;
                }

                /* Scrollable List Sections */
                .ms-android-scrollable {
                    flex: 1 !important;
                    overflow-y: auto !important;
                    overflow-x: hidden !important;
                    touch-action: pan-y !important;
                    -webkit-overflow-scrolling: touch !important;
                    padding: 8px 0 16px 0 !important;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }

                .ms-android-section-title {
                    font-size: 10.5px !important;
                    font-weight: 800 !important;
                    letter-spacing: 1px !important;
                    color: #FFFFFF !important;
                    padding: 12px 18px 6px 18px !important;
                    margin: 0 !important;
                    text-transform: uppercase !important;
                    box-sizing: border-box !important;
                }

                .ms-android-item {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    padding: 11px 18px !important;
                    color: #E5E5E5 !important;
                    font-size: 13.5px !important;
                    font-weight: 500 !important;
                    text-decoration: none !important;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.03) !important;
                    background: transparent !important;
                    border-left: none !important;
                    border-right: none !important;
                    border-top: none !important;
                    width: 100% !important;
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                    cursor: pointer !important;
                    text-align: inherit !important;
                    transition: background 0.15s ease !important;
                }

                .ms-android-item:active {
                    background: rgba(255, 255, 255, 0.05) !important;
                    color: #FE2C55 !important;
                }

                .ms-android-item-left {
                    display: flex !important;
                    align-items: center !important;
                    gap: 12px !important;
                }

                .ms-android-item-icon {
                    color: #888888 !important;
                    flex-shrink: 0 !important;
                }

                .ms-android-chevron {
                    color: #555555 !important;
                    flex-shrink: 0 !important;
                }

                html[dir="rtl"] .ms-android-chevron {
                    transform: rotate(180deg) !important;
                }

                .ms-android-divider {
                    height: 1px !important;
                    background: #1F1F22 !important;
                    margin: 8px 0 !important;
                    border: none !important;
                }

                /* Footer */
                .ms-android-footer {
                    background: #131316 !important;
                    border-top: 1px solid #1F1F22 !important;
                    padding: 14px 16px !important;
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 10px !important;
                }

                .ms-android-footer-row {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                }

                .ms-android-lang-pill {
                    display: inline-flex !important;
                    align-items: center !important;
                    gap: 6px !important;
                    padding: 5px 12px !important;
                    background: #1F1F22 !important;
                    border: 1px solid #333333 !important;
                    border-radius: 20px !important;
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    color: #FFFFFF !important;
                }

                .ms-android-version {
                    font-size: 10px !important;
                    font-weight: 500 !important;
                    color: #888888 !important;
                }

                .ms-android-logout-btn {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 6px !important;
                    width: 100% !important;
                    height: 36px !important;
                    background: transparent !important;
                    border: none !important;
                    color: #EF5350 !important;
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    cursor: pointer !important;
                }
            `}} />

            {/* Backdrop */}
            <div
                className={`ms-android-backdrop ${isOpen ? 'open' : ''}`}
                onClick={handleClose}
            />

            {/* Modal Drawer Sheet */}
            <div
                className={`ms-android-drawer ${isOpen ? 'open' : ''} ${isAr ? 'rtl-mode' : 'ltr-mode'}`}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                {/* Gradient Obsidian Profile Header */}
                <div className="ms-android-header">
                    <div className="ms-android-brand-row">
                        <span className="ms-android-brand">LUXVONO</span>
                        <button
                            ref={closeBtnRef}
                            onClick={handleClose}
                            className="ms-android-close-btn"
                            aria-label="Close menu"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="ms-android-user-row">
                        <div className="ms-android-avatar">
                            {userInitial}
                        </div>
                        <div className="ms-android-user-meta">
                            <span className="ms-android-username">
                                {isAuthenticated ? userName : (isAr ? 'مرحباً بك، زائر' : 'Welcome Guest')}
                            </span>
                            {isAuthenticated ? (
                                <div className="ms-android-badge">
                                    <div className="ms-android-badge-dot" />
                                    <span>{isAr ? 'عضوية فاخرة' : 'LUXE MEMBER'}</span>
                                </div>
                            ) : (
                                <span className="ms-android-guest-sub">
                                    {isAr ? 'سجل الدخول لتجربة متكاملة' : 'Login for exclusive features'}
                                </span>
                            )}
                        </div>
                    </div>

                    {!isAuthenticated && (
                        <Link to="/login" onClick={onClose} className="ms-android-login-btn">
                            {isAr ? 'تسجيل الدخول / التسجيل' : 'Login / Register'}
                        </Link>
                    )}
                </div>

                {/* Alibaba-Style Horizontal Quick Actions Bar */}
                <div className="ms-android-quick-bar">
                    <Link to={isAuthenticated ? "/orders" : "/login"} onClick={onClose} className="ms-android-quick-card">
                        <List size={18} className="ms-android-quick-icon" />
                        <span className="ms-android-quick-label">{isAr ? 'طلباتي' : 'Orders'}</span>
                    </Link>
                    <Link to="/cart" onClick={onClose} className="ms-android-quick-card">
                        <ShoppingBag size={18} className="ms-android-quick-icon" />
                        <span className="ms-android-quick-label">{isAr ? 'السلة' : 'Cart'}</span>
                    </Link>
                    <Link to="/wishlist" onClick={onClose} className="ms-android-quick-card">
                        <Heart size={18} className="ms-android-quick-icon" />
                        <span className="ms-android-quick-label">{isAr ? 'المفضلة' : 'Wishlist'}</span>
                    </Link>
                    <button type="button" onClick={openSupportChat} className="ms-android-quick-card" style={{ background: 'transparent' }}>
                        <Info size={18} className="ms-android-quick-icon" />
                        <span className="ms-android-quick-label">{isAr ? 'المساعدة' : 'Help'}</span>
                    </button>
                </div>

                {/* Scrollable Navigation Sections */}
                <div className="ms-android-scrollable">
                    {/* Section 0: Live Stage & Interactive Commerce (Prominently featured) */}
                    <div className="ms-android-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{isAr ? 'المسرح والبث المباشر 🔴' : 'LIVE & INTERACTIVE 🔴'}</span>
                    </div>

                    <Link to="/live" onClick={onClose} className="ms-android-item" style={{ background: 'rgba(254, 44, 85, 0.06)' }}>
                        <div className="ms-android-item-left">
                            <Radio size={18} style={{ color: '#FE2C55' }} />
                            <span style={{ fontWeight: '800', color: '#FFFFFF' }}>
                                {isAr ? 'مسرح البث المباشر الفاخر' : 'LUXVONO Live Stage'}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                background: 'rgba(254, 44, 85, 0.2)',
                                border: '1px solid rgba(254, 44, 85, 0.4)',
                                color: '#FE2C55',
                                fontSize: '10px',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontWeight: '800',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <span className="ms-live-dot" />
                                {isAr ? 'مباشر' : 'LIVE'}
                            </span>
                            <ChevronRight size={14} className="ms-android-chevron" />
                        </div>
                    </Link>

                    <Link to="/videos" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Video size={18} style={{ color: '#A78BFA' }} />
                            <span>{isAr ? 'تسوق الفيديوهات الحصرية' : 'Luxury Video Reels'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                background: 'rgba(167, 139, 250, 0.15)',
                                color: '#A78BFA',
                                fontSize: '10px',
                                padding: '2px 7px',
                                borderRadius: '10px',
                                fontWeight: '700'
                            }}>
                                REELS
                            </span>
                            <ChevronRight size={14} className="ms-android-chevron" />
                        </div>
                    </Link>

                    <Link to="/vip" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Crown size={18} style={{ color: '#F59E0B' }} />
                            <span>{isAr ? 'صالون VIP والخدمات الخاصة' : 'VIP Private Lounge'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <Link to="/stores" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Store size={18} style={{ color: '#10B981' }} />
                            <span>{isAr ? 'دليل المتاجر والوكلاء المعتمدين' : 'Verified Stores & Brands'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <Link to="/following" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Users size={18} style={{ color: '#38BDF8' }} />
                            <span>{isAr ? 'المتاجر التي أتابعها' : 'Stores I Follow'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <Link to="/magazine" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <BookOpen size={18} style={{ color: '#E2E8F0' }} />
                            <span>{isAr ? 'مجلة لوكسفونو للأزياء والرفاهية' : 'LUXVONO Magazine'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <hr className="ms-android-divider" />

                    {/* Section 1: Store Categories & Collections */}
                    <div className="ms-android-section-title">
                        {isAr ? 'أقسام وتشكيلات المتجر ✦' : 'STORE COLLECTIONS ✦'}
                    </div>

                    <Link to="/category/all" onClick={onClose} className="ms-android-item" style={{ color: '#FFFFFF', fontWeight: '700' }}>
                        <div className="ms-android-item-left">
                            <ShoppingBag size={18} style={{ color: '#FFFFFF' }} />
                            <span>{isAr ? 'استعراض كافة الأقسام والكتالوج' : 'Explore All Collections'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    {displayCategories.map(cat => (
                        <Link
                            key={cat.id}
                            to={cat.path}
                            onClick={onClose}
                            className="ms-android-item"
                        >
                            <span>{cat.name}</span>
                            <ChevronRight size={14} className="ms-android-chevron" />
                        </Link>
                    ))}

                    <hr className="ms-android-divider" />

                    {/* Section 2: Services & Wholesale Portal */}
                    <div className="ms-android-section-title">
                        {isAr ? 'الخدمات الحصرية والتوريد' : 'SERVICES & WHOLESALE'}
                    </div>

                    <Link to="/sourcing" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Globe size={18} className="ms-android-item-icon" />
                            <span>{isAr ? 'سوق الجملة والتوريد العالمي 🌍' : 'Wholesale B2B Marketplace 🌍'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <div style={{ padding: '6px 12px', width: '100%', boxSizing: 'border-box' }}>
                        <Link 
                            to="/seller/dashboard" 
                            onClick={onClose} 
                            className="ms-android-item ms-vendor-highlight-item"
                            style={{
                                background: '#1A1C24',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                borderRadius: '12px',
                                padding: '12px 14px',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}
                        >
                            <div className="ms-android-item-left" style={{ gap: '12px' }}>
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '10px',
                                    background: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#000000',
                                    flexShrink: 0,
                                    boxShadow: '0 2px 8px rgba(255,255,255,0.08)'
                                }}>
                                    <Store size={20} color="#000000" />
                                </div>
                                <div>
                                    <div style={{ fontWeight: '800', fontSize: '14px', color: '#FFFFFF' }}>
                                        {isAr ? '🏛️ دخول البائعين والتجار' : '🏛️ Seller & Vendor Portal'}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '1px' }}>
                                        {isAr ? 'لوحة التحكم، المنتجات والأرباح' : 'Dashboard, listings & payouts'}
                                    </div>
                                </div>
                            </div>
                            <span style={{
                                background: '#FFFFFF',
                                color: '#000000',
                                fontSize: '11px',
                                fontWeight: '800',
                                padding: '3px 10px',
                                borderRadius: '20px',
                                letterSpacing: '0.5px'
                            }}>
                                {isAr ? 'دخول' : 'Enter'}
                            </span>
                        </Link>
                    </div>

                    <Link to={isAuthenticated ? "/orders" : "/login"} onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <List size={18} className="ms-android-item-icon" />
                            <span>{isAr ? 'طلباتي وسجل الفواتير' : 'Orders & Invoices'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <Link to="/wishlist" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Heart size={18} className="ms-android-item-icon" />
                            <span>{isAr ? 'المفضلة والمحفوظات' : 'Wishlist & Saved'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <hr className="ms-android-divider" />

                    {/* Section 3: About & Policies */}
                    <div className="ms-android-section-title">
                        {isAr ? 'عن الشركة والسياسات المعتمدة' : 'ABOUT & POLICIES'}
                    </div>

                    <Link to="/about" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Info size={18} className="ms-android-item-icon" />
                            <span>{isAr ? 'من نحن ورؤيتنا الفاخرة' : 'About Us & Heritage'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <Link to="/shipping" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Truck size={18} className="ms-android-item-icon" />
                            <span>{isAr ? 'الشحن والتوصيل الدولي المصفح' : 'Worldwide Shipping & Delivery'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <Link to="/returns" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <ShieldCheck size={18} className="ms-android-item-icon" />
                            <span>{isAr ? 'الاسترجاع وضمان التداول (Escrow)' : 'Returns & 100% Escrow Protection'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <Link to="/help" onClick={onClose} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <HelpCircle size={18} className="ms-android-item-icon" />
                            <span>{isAr ? 'مركز المساعدة والأسئلة الشائعة' : 'Help Center & FAQ'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </Link>

                    <button type="button" onClick={openSupportChat} className="ms-android-item">
                        <div className="ms-android-item-left">
                            <Headphones size={18} className="ms-android-item-icon" style={{ color: '#10B981' }} />
                            <span>{isAr ? 'المحادثة الفورية مع الكونسيرج' : 'Live Concierge Chat'}</span>
                        </div>
                        <ChevronRight size={14} className="ms-android-chevron" />
                    </button>
                </div>

                {/* Global Sourcing Footer Panel */}
                <div className="ms-android-footer">
                    <div className="ms-android-footer-row">
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <LanguageToggle />
                            <RegionSelector />
                        </div>
                        <span className="ms-android-version">v2.4.0 - Global</span>
                    </div>

                    {isAuthenticated && (
                        <button
                            type="button"
                            onClick={() => {
                                logout();
                                onClose();
                            }}
                            className="ms-android-logout-btn"
                        >
                            <LogOut size={16} />
                            <span>{isAr ? 'تسجيل الخروج من الحساب' : 'Sign Out of Account'}</span>
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default MobileSidebar;
